import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface Newsletter {
  id: string
  title: string
  subject?: string
  status: string
  renderedHtml?: string
  previewUrl?: string
}

interface NewsletterPreviewProps {
  newsletter: Newsletter
}

export default function NewsletterPreview({ newsletter }: NewsletterPreviewProps) {
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop")
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [previewHtml, setPreviewHtml] = useState(newsletter.renderedHtml || "")

  const generatePreview = trpc.newsletter.requestApproval.useMutation({
    onSuccess: (result) => {
      setPreviewHtml(result.previewUrl || "")
      setIsGeneratingPreview(false)
    },
    onError: (error) => {
      alert(`Failed to generate preview: ${error.message}`)
      setIsGeneratingPreview(false)
    },
  })

  const handleGeneratePreview = () => {
    setIsGeneratingPreview(true)
    generatePreview.mutate(newsletter.id)
  }

  // Create a simple HTML preview if we don't have rendered HTML
  const createSimplePreview = () => {
    return `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${newsletter.subject || newsletter.title}</title>
        <style>
            body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                line-height: 1.6;
                color: #333;
                max-width: 600px;
                margin: 0 auto;
                padding: 20px;
                background-color: #f9f9f9;
            }
            .container {
                background-color: white;
                padding: 30px;
                border-radius: 8px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header {
                text-align: center;
                border-bottom: 2px solid #eee;
                padding-bottom: 20px;
                margin-bottom: 30px;
            }
            .header h1 {
                color: #2563eb;
                margin-bottom: 10px;
            }
            .preview-notice {
                background: #fef3c7;
                border: 1px solid #f59e0b;
                border-radius: 6px;
                padding: 15px;
                margin-bottom: 20px;
                text-align: center;
            }
            .preview-notice h3 {
                color: #92400e;
                margin: 0 0 8px 0;
            }
            .preview-notice p {
                color: #78350f;
                margin: 0;
                font-size: 14px;
            }
        </style>
    </head>
    <body>
        <div class="container">
            <div class="header">
                <h1>${newsletter.title}</h1>
                <p><em>Newsletter Preview</em></p>
            </div>

            <div class="preview-notice">
                <h3>⚠️ Preview Mode</h3>
                <p>This is a preview of your newsletter. Generate the full preview to see aggregated content.</p>
            </div>

            <p>This newsletter is currently being prepared with content from your selected sources.</p>
            <p>Click "Generate Preview" to see the complete newsletter with all aggregated content.</p>
        </div>
    </body>
    </html>
    `
  }

  const displayHtml = previewHtml || createSimplePreview()

  return (
    <div className="space-y-6">
      {/* Preview Controls */}
      <div className="bg-background rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold ">Newsletter Preview</h2>

          <div className="flex items-center space-x-4">
            {/* Device Toggle */}
            <div className="flex bg-foregroundx rounded-lg p-1">
              <button
                onClick={() => setPreviewMode("desktop")}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  previewMode === "desktop" ? "bg-background  shadow-sm" : "text-gray-500 hover:text-primary"
                }`}
              >
                Desktop
              </button>
              <button
                onClick={() => setPreviewMode("mobile")}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  previewMode === "mobile" ? "bg-background  shadow-sm" : "text-gray-500 hover:text-primary"
                }`}
              >
                Mobile
              </button>
            </div>

            {/* Generate Preview Button */}
            <button
              onClick={handleGeneratePreview}
              disabled={isGeneratingPreview}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isGeneratingPreview ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Generate Preview
                </>
              )}
            </button>
          </div>
        </div>

        {newsletter.previewUrl && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-800">
              ✅ Preview generated successfully!
              <a
                href={newsletter.previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-green-600 hover:text-green-800 underline"
              >
                View in new tab
              </a>
            </p>
          </div>
        )}
      </div>

      {/* Preview Frame */}
      <div className="bg-background rounded-lg shadow overflow-hidden">
        <div className="bg-foregroundx px-4 py-2 border-b">
          <div className="flex items-center space-x-2">
            <div className="flex space-x-1">
              <div className="w-3 h-3 bg-red-400 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            </div>
            <div className="flex-1 bg-background rounded px-3 py-1 text-sm ">
              {newsletter.subject || newsletter.title} - Email Preview
            </div>
          </div>
        </div>

        <div className="flex justify-center bg-foregroundx p-8">
          <div
            className={`bg-background rounded-lg shadow-lg overflow-hidden transition-all duration-300 ${
              previewMode === "desktop" ? "w-full max-w-4xl" : "w-80"
            }`}
            style={{
              height: "600px",
              maxHeight: "80vh",
            }}
          >
            <iframe
              srcDoc={displayHtml}
              className="w-full h-full border-0"
              title="Newsletter Preview"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      </div>

      {/* Preview Actions */}
      <div className="bg-background rounded-lg shadow p-6">
        <h3 className="text-lg font-medium  mb-4">Preview Actions</h3>

        <div className="flex items-center justify-between">
          <div className="text-sm ">
            <p>
              Newsletter Status: <span className="font-medium">{newsletter.status}</span>
            </p>
            {newsletter.previewUrl && (
              <p className="mt-1">
                Share preview:
                <button
                  onClick={() => {
                    void navigator.clipboard.writeText(newsletter.previewUrl)
                    alert("Preview URL copied to clipboard!")
                  }}
                  className="ml-2  hover:text-blue-800 underline"
                >
                  Copy link
                </button>
              </p>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => window.open(newsletter.previewUrl || "#", "_blank")}
              disabled={!newsletter.previewUrl}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-primary bg-background  focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              Open in New Tab
            </button>

            {newsletter.status === "draft" && (
              <button
                onClick={handleGeneratePreview}
                disabled={isGeneratingPreview}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                Ready for Approval
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
